import { useLocation } from 'react-router-dom'

import { deleteParamsFromUrl, getUrlWithParams } from 'core-components/helpers/url'
import {
  FYEAR_URL,
  OFFERING_URL,
  P360_GLOBAL_PAGE_URL,
  P360_PAGE_URL,
  PID_URL,
  TAB_URL,
} from 'partner360/configs'
import { getUrlParamsForDeleting } from 'partner360/helpers/routing/getUrlParamsForDeleting'
import { VIEW_MODE_URL_PARAM_KEY } from 'partner360/hooks/viewModeUrlParam'

export interface IPartnerPlanUrlParams {
  partnerId: string
  programType?: string
  tabUrl?: string
  isGlobal?: boolean
  fYear?: number
  /** `v` — which planning view the partner page opens in. Omitted, the page decides for itself. */
  viewMode?: string
  preserveCurrentUrlParams?: boolean
}

const removeEmptyParams = <T extends Record<string, unknown>>(params: T) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  ) as Partial<T>

/**
 * The URL of a partner's own plan page, as every link to it is built.
 *
 * Kept apart from `PartnerNameWithLink` so a caller that draws something other than the name — an
 * icon, say — reaches the same address rather than assembling one of its own.
 */
export const usePartnerPlanUrl = ({
  partnerId,
  programType,
  tabUrl,
  isGlobal,
  fYear,
  viewMode,
  preserveCurrentUrlParams,
}: IPartnerPlanUrlParams): string => {
  const location = useLocation()

  const urlParams = removeEmptyParams({
    page: isGlobal ? P360_GLOBAL_PAGE_URL : P360_PAGE_URL,
    [PID_URL]: encodeURIComponent(partnerId),
    [FYEAR_URL]: fYear == null ? undefined : encodeURIComponent(String(fYear)),
    [OFFERING_URL]: programType ? encodeURIComponent(programType) : undefined,
    [VIEW_MODE_URL_PARAM_KEY]: viewMode,
    [TAB_URL]: tabUrl ? encodeURIComponent(tabUrl) : undefined,
  })

  /** The current page's own params are stripped so its state doesn't leak into the partner link —
   *  but never the ones this link explicitly sets. Without that exclusion the deletion pass eats
   *  what the caller just asked for: a `fYear` passed from a page whose own URL carries `fyear` was
   *  silently dropped, and from any partner-scoped page (`/pid/...`) even `pid` was, leaving a link
   *  to the bare page. */
  return preserveCurrentUrlParams
    ? getUrlWithParams(urlParams)
    : deleteParamsFromUrl(
        getUrlParamsForDeleting(location.pathname).filter((key) => !(key in urlParams)),
        getUrlWithParams(urlParams)
      )
}
